/**
 * Every environment variable the app reads, in one place.
 *
 * This file exists so that switching build tools - create-react-app's `process.env.REACT_APP_*`
 * today, Vite's `import.meta.env.VITE_*` next (#3706) - changes only this file within `src/`. No
 * other application module in `src/` reads `process.env` (`pnpm lint` checks); import from here
 * instead. The variable names are also set outside `src/` - by .github/actions/client-deploy,
 * .github/workflows/PR_Client_Playwright.yml and .env.development.local (see check-env.js) - and
 * read by the Playwright config, which runs in Node; a rename has to change those too.
 *
 * Each value is read as a literal `process.env.NAME` expression on purpose. The build replaces
 * these expressions with the values at build time, and a literal read is the one form every
 * build tool replaces reliably; aliasing `process.env` or reading it with a computed key can
 * compile fine and still come out undefined in the bundle.
 *
 * Values are parsed here, once, into what the app uses: a flag is a boolean that is true only for
 * the string 'true', a timer interval falls back to its default when it is missing or unusable, and
 * anything else is the string the build provides, or undefined when it is not set.
 */

// The shortest interval allowed. Anything faster polls the API from every open tab more than once
// a second, which is a misconfiguration rather than a setting.
const MIN_TIMER_MS = 1000
// Browser timers take a signed 32-bit millisecond delay; a longer one fires straight away.
const MAX_TIMER_MS = 2147483647

// A timer interval in seconds, read the way JavaScript reads a number - '60', ' 60 ', '1.5', '60.'
// - as the call site's `value * 1000` always did, and accepted from 1 second up to the timer's
// limit. Anything else - '0', '-3', '0.001', a typo such as '6O', a value past the limit - gives
// the fallback instead, with a warning when the variable is set, so the mistake shows up.
const timerSecondsOr = (name, value, fallback) => {
  if (value === undefined || value.trim() === '') return fallback
  const seconds = Number(value)
  // Checked on `seconds * 1000`, the same product the call site hands to the timer.
  if (
    Number.isFinite(seconds) &&
    seconds * 1000 >= MIN_TIMER_MS &&
    seconds * 1000 <= MAX_TIMER_MS
  ) {
    return seconds
  }
  console.warn(
    `${name} is ${JSON.stringify(
      value
    )}, not a usable number of seconds; using ${fallback}.`
  )
  return fallback
}

// Set by the build: `production` for `react-scripts build`, `development` for `start`, `test` for jest.
export const isProduction = process.env.NODE_ENV === 'production'

// Where the app is served from; empty string when it is served from the root.
export const PUBLIC_URL = process.env.PUBLIC_URL

// APIs
export const BACKEND_API = process.env.REACT_APP_BACKEND_API
export const GT_API = process.env.REACT_APP_GT_API

// Azure AD sign-in (MSAL)
export const USE_AD = process.env.REACT_APP_USE_AD === 'true'
export const MSAL_CONFIG_AUTHORITY = process.env.REACT_APP_MSAL_CONFIG_AUTHORITY
export const MSAL_CONFIG_CLIENT_ID = process.env.REACT_APP_MSAL_CONFIG_CLIENT_ID
export const MSAL_CONFIG_REDIRECT_URL =
  process.env.REACT_APP_MSAL_CONFIG_REDIRECT_URL
export const MSAL_CONFIG_EXPOSED_API_SCOPE =
  process.env.REACT_APP_MSAL_CONFIG_EXPOSED_API_SCOPE
export const LOGIN_STATE = process.env.REACT_APP_LOGIN_STATE || 'login'

// Lemonsoft B2C sign-in
export const USE_LEMONSOFT_B2C =
  process.env.REACT_APP_USE_LEMONSOFT_B2C === 'true'
export const LEMONSOFT_B2C_AUTHORITY =
  process.env.REACT_APP_LEMONSOFT_B2C_AUTHORITY
export const LEMONSOFT_B2C_CLIENT_ID =
  process.env.REACT_APP_LEMONSOFT_B2C_CLIENT_ID
export const LEMONSOFT_B2C_REDIRECT_URL =
  process.env.REACT_APP_LEMONSOFT_B2C_REDIRECT_URL
export const LEMONSOFT_B2C_SCOPE = process.env.REACT_APP_LEMONSOFT_B2C_SCOPE

// Development conveniences: a flag for hiding things in production, and a prefilled login
export const IS_DEVELOPMENT = process.env.REACT_APP_IS_DEVELOPMENT === 'true'
export const USERNAME = process.env.REACT_APP_USERNAME
export const PASSWORD = process.env.REACT_APP_PASSWORD

// How often notifications are polled. Defaults to 5 seconds, also for 0 or a value that is not a
// number, either of which would otherwise poll the API continuously.
export const NOTIFICATIONS_UPDATE_IN_SECONDS = timerSecondsOr(
  'REACT_APP_NOTIFICATIONS_UPDATE_IN_SECONDS',
  process.env.REACT_APP_NOTIFICATIONS_UPDATE_IN_SECONDS,
  5
)

// Hotjar
export const HOTJAR_SITE_ID = process.env.REACT_APP_HOTJAR_SITE_ID
export const HOTJAR_VERSION = process.env.REACT_APP_HOTJAR_VERSION

// Flexmonster licence keys, chosen by hostname in components/Flexmonster/constants.js
export const FLEXMONSTER_LICENSE_KEY =
  process.env.REACT_APP_FLEXMONSTER_LICENSE_KEY
export const FLEXMONSTER_LICENSE_KEY_FINAZILLA_APP_DOMAIN =
  process.env.REACT_APP_FLEXMONSTER_LICENSE_KEY_FINAZILLA_APP_DOMAIN
export const FLEXMONSTER_LICENSE_KEY_FINAZILLA_DEV_DOMAIN =
  process.env.REACT_APP_FLEXMONSTER_LICENSE_KEY_FINAZILLA_DEV_DOMAIN

// Integrations
export const FINVOICER_URL = process.env.REACT_APP_FINVOICER_URL
export const FINVOICER_CLIENT_ID = process.env.REACT_APP_FINVOICER_CLIENT_ID
export const XERO_CLIENT_ID = process.env.REACT_APP_XERO_CLIENT_ID
